import { api, type Page } from "@/lib/api/client";
import type { ApprovalInstance, InboxItem, RerouteResult } from "./approvals.types";

export const approvalsApi = {
  /** The caller's own open tasks. Whose inbox it is comes from the session, never a parameter. */
  inbox(params: {
    page?: number;
    limit?: number;
    sortBy?: "dueAt" | "createdAt" | "stepNo";
    sortOrder?: "asc" | "desc";
    subjectType?: string;
    overdue?: boolean;
  }): Promise<Page<InboxItem>> {
    return api.page<InboxItem>("/workflow/approvals/inbox", { params });
  },

  /**
   * The approval behind one business record — "why is this waiting, and on whom".
   *
   * `includeClosed` is on wherever a history is shown: a finished chain is exactly what the
   * reader wants to see after the fact.
   */
  forSubject(params: {
    entityType: string;
    entityId: string;
    includeClosed?: boolean;
  }): Promise<ApprovalInstance[]> {
    return api.get<ApprovalInstance[]>("/workflow/approvals", { params });
  },

  get(id: string): Promise<ApprovalInstance> {
    return api.get<ApprovalInstance>(`/workflow/approvals/${id}`);
  },

  approve(taskId: string, comment?: string): Promise<ApprovalInstance> {
    return api.post<ApprovalInstance>(
      `/workflow/tasks/${taskId}/approve`,
      comment ? { comment } : {},
    );
  },

  /**
   * «Yeniden yönlendir» (onay-zinciri 20 Ekim 2026): a stuck approval — its holder's account is gone, closed or
   * its owner left — is re-resolved against today's organisation chart. No body: the engine's own rules pick the
   * holder, the caller chooses nobody. Needs `workflow.approval.reroute`.
   */
  reroute(instanceId: string): Promise<RerouteResult> {
    return api.post<RerouteResult>(`/workflow/approvals/${instanceId}/reroute`, {});
  },

  /** A rejection carries a mandatory reason: the requester cannot act on a bare "no". */
  reject(taskId: string, comment: string): Promise<ApprovalInstance> {
    return api.post<ApprovalInstance>(`/workflow/tasks/${taskId}/reject`, { comment });
  },
};
